import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useLoginMutation } from '../../store/slices/apiSlice';
import { clearAuthError, loginFailure, loginStart, loginSuccess } from '../../store/slices/authSlice';
import { type LoginFormData, loginSchema } from '../../utils/validation';
import type { LoginFormProps } from '../../types';

export const LoginForm: React.FC<LoginFormProps> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  
  // Get auth state from Redux to show persistent errors
  const { error: authError } = useAppSelector(state => state.auth);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      dispatch(loginStart());
      // Clear any previous errors
      setLoginError(null);
      clearErrors('root');
      
      const result = await login(data).unwrap();
      dispatch(loginSuccess(result));
      // Redirect to root which will handle role-based routing
      navigate('/');
    } catch (error: any) {
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      // Handle specific error messages from backend
      if (error?.data?.non_field_errors?.length > 0) {
        errorMessage = error.data.non_field_errors[0];
      } else if (error?.data?.detail) {
        errorMessage = error.data.detail;
      } else if (error?.status === 401) {
        errorMessage = 'Invalid username or password. Please check your credentials and try again.';
      } else if (error?.status === 403) {
        errorMessage = 'Access denied. Your account may be blocked or inactive.';
      } else if (error?.status >= 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (error?.status === 0 || !error?.status) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      dispatch(loginFailure(errorMessage));
      setLoginError(errorMessage);
      setError('root', { message: errorMessage });
    }
  };

  // Use auth error from Redux or local error (whichever exists)
  const displayError = authError || loginError;

  // Clear errors when user starts typing
  const handleInputChange = () => {
    if (loginError || authError) {
      setLoginError(null);
      clearErrors('root');
      // Clear auth error
      if (authError) {
        dispatch(clearAuthError());
      }
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900">Log in</h2>
      </div>

      {/* Persistent Error Display - Above Form */}
      {displayError && (
        <div className="mb-6 p-4 border border-red-300 rounded-lg bg-red-50">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800">Login Failed</h3>
              <p className="text-sm text-red-700 mt-1">
                {displayError}
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Username Field */}
        <div className="form-group">
          <label htmlFor="username" className="form-label">
            Username
          </label>
          <input
            id="username"
            type="text"
            placeholder="Enter your username"
            className={`form-input ${errors.username
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              }`}
            {...register('username')}
            onChange={(e) => {
              register('username').onChange(e);
              handleInputChange();
            }}
          />
          {errors.username && (
            <p className="form-error">{errors.username.message}</p>
          )}
        </div>

        {/* Password Field */}
        <div className="form-group">
          <label htmlFor="password" className="form-label">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              className={`form-input pr-12 ${errors.password
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
              {...register('password')}
              onChange={(e) => {
                register('password').onChange(e);
                handleInputChange();
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center justify-center w-12 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="form-error">{errors.password.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full btn btn-primary flex items-center justify-center py-3 text-base font-medium"
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
};